/**
 * 送る前の入力検証（docs/05 §11.8）。
 *
 * <b>判定の正本は API</b>（サーバの ViewModel の DataAnnotations）。ここは送る前の手助けで、
 * 通しても API が改めて全部見る。<b>API より厳しくしない</b>——API が通す値をここで止めると、
 * 利用者は理由が分からないまま送れなくなる。
 *
 * 規則は属性の写し（<c>view-model-rules.ts</c>）。文言も属性の ErrorMessage と Display の Name から
 * API と同じ組み立てで作る（画面と API で違う文を出さない）。
 */

/** <c>[StringLength(max, MinimumLength, ErrorMessage)]</c>。文言の <c>{1}</c> が max、<c>{2}</c> が min。 */
export interface LengthRule {
    readonly max: number;
    readonly min?: number;
    readonly message: string;
}

/** 1 欄の規則。<b>属性 1 つに 1 項目</b>で、書いたものだけを見る。 */
export interface FieldRules {
    /** <c>[Display(Name)]</c>。文言の <c>{0}</c>。 */
    readonly display: string;
    /** <c>[Required(ErrorMessage)]</c>。 */
    readonly required?: string;
    /** <c>[EmailAddress(ErrorMessage)]</c>。 */
    readonly email?: string;
    readonly length?: LengthRule;
    /** <c>[Compare(other, ErrorMessage)]</c>。 */
    readonly compare?: { readonly other: string; readonly message: string };
    /** <c>[Range(typeof(bool), "true", "true", ErrorMessage)]</c>（同意のチェックボックス）。 */
    readonly mustBeTrue?: string;
    /**
     * 形式（<c>Validate</c> の中で見ているもの。ご契約者の郵便番号・電話番号）。<c>valid</c> が false なら <c>message</c>。
     * 判定は API と同じもの（<c>billing-formats.ts</c>）を渡す。
     */
    readonly format?: { readonly valid: (value: string) => boolean; readonly message: string };
}

/** 欄の名前 → 規則。<b>画面に並ぶ順に書く</b>（最初に断った欄へ移る）。 */
export type FormRules = Readonly<Record<string, FieldRules>>;

/** 欄の値を読む。チェックボックスは "true" / "false"。 */
export type ValueOf = (name: string) => string;

/** 欄の名前 → 断り。 */
export type ClientErrors = Readonly<Record<string, string>>;

/** ASP.NET Core と同じく <c>{0}</c>・<c>{1}</c>・<c>{2}</c> を差し込む。 */
const format = (template: string, ...args: readonly (string | number)[]): string =>
    template.replaceAll(/\{(\d)\}/gu, (whole, index: string) => {
        const arg = args[Number(index)];
        return arg === undefined ? whole : String(arg);
    });

/** RequiredAttribute は空白だけの文字も空とみなす。 */
const isBlank = (value: string): boolean => value.trim().length === 0;

/**
 * EmailAddressAttribute と同じ判定。<b>「@ が 1 つだけで、先頭でも末尾でもない」だけ</b>を見る。
 * 厳しくすると、API が通すアドレスをここで止めてしまう。
 */
export const isEmailLike = (value: string): boolean => {
    const at = value.indexOf("@");
    return at > 0 && at !== value.length - 1 && at === value.lastIndexOf("@");
};

/**
 * 1 欄の最初の断り。無ければ undefined。
 * <b>空の欄は <c>required</c> 以外の規則では断らない</b>（API のモデルバインドは空を null にし、
 * null は StringLength・EmailAddress を通る）。<c>compare</c> だけは空でも比べる（null と値は一致しない）。
 */
export const violationOf = (name: string, rules: FormRules, valueOf: ValueOf): string | undefined => {
    const rule = rules[name];
    if (rule === undefined) {
        return undefined;
    }
    const value = valueOf(name);
    const { display } = rule;

    if (rule.required !== undefined && isBlank(value)) {
        return format(rule.required, display);
    }
    if (rule.email !== undefined && value !== "" && !isEmailLike(value)) {
        return format(rule.email, display);
    }
    if (rule.length !== undefined && value !== "") {
        const { max, min = 0, message } = rule.length;
        if (value.length > max || value.length < min) {
            return format(message, display, max, min);
        }
    }
    // 形式は長さの後——API では長さ（属性）で断ると Validate（形式）まで進まない
    if (rule.format !== undefined && !isBlank(value) && !rule.format.valid(value)) {
        return format(rule.format.message, display);
    }
    if (rule.compare !== undefined && value !== valueOf(rule.compare.other)) {
        return format(rule.compare.message, display);
    }
    if (rule.mustBeTrue !== undefined && value !== "true") {
        return format(rule.mustBeTrue, display);
    }
    return undefined;
};

/** 全部の欄を見る。<b>断った欄だけ</b>を返す（規則の順）。 */
export const violationsOf = (rules: FormRules, valueOf: ValueOf): ClientErrors => {
    const errors: Record<string, string> = {};
    for (const name of Object.keys(rules)) {
        const message = violationOf(name, rules, valueOf);
        if (message !== undefined) {
            errors[name] = message;
        }
    }
    return errors;
};
